import { useCallback, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BookOpen, Code2, Eye, LayoutDashboard, LogOut, Menu, Moon, Settings, Sun, X } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useTheme } from "../theme/ThemeProvider";
import type { Lang } from "../i18n/strings";

interface NavDef {
  to: string;
  i18nKey: string;
  icon: JSX.Element;
}

const NAV: NavDef[] = [
  {
    to: "/stats",
    i18nKey: "nav_stats",
    icon: <LayoutDashboard size={16} />,
  },
  {
    to: "/settings",
    i18nKey: "nav_settings",
    icon: <Settings size={16} />,
  },
  {
    to: "/preview",
    i18nKey: "nav_preview",
    icon: <Eye size={16} />,
  },
  {
    to: "/kb",
    i18nKey: "nav_kb",
    icon: <BookOpen size={16} />,
  },
  {
    to: "/integration",
    i18nKey: "nav_integration",
    icon: <Code2 size={16} />,
  },
];

export function AppShell() {
  const { session, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, toggle: toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  return (
    <div className="shell is-visible" id="app-shell">
      <header className="mobile-topbar">
        <button type="button" className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <Menu size={20} />
        </button>
        <div className="mobile-brand">
          <div className="sidebar-mark">
            <svg viewBox="0 0 24 24" fill="white" width="12" height="12">
              <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 110 2h-1v1a3 3 0 01-3 3H7a3 3 0 01-3-3v-1H3a1 1 0 110-2h1a7 7 0 017-7h1V5.73A2 2 0 0112 2zM9.5 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm5 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
            </svg>
          </div>
          <span className="sidebar-name">AM AI Assistant</span>
        </div>
      </header>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeSidebar} />}

      <aside className={"sidebar" + (sidebarOpen ? " is-open" : "")}>
        <div className="sidebar-brand">
          <div className="sidebar-mark">
            <svg viewBox="0 0 24 24" fill="white" width="14" height="14">
              <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 110 2h-1v1a3 3 0 01-3 3H7a3 3 0 01-3-3v-1H3a1 1 0 110-2h1a7 7 0 017-7h1V5.73A2 2 0 0112 2zM9.5 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm5 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
            </svg>
          </div>
          <div className="sidebar-tenant">
            <div className="sidebar-platform-name">
              <span className="brand-ams">AM</span> AI Assistant
            </div>
            <div className="sidebar-slug">{session?.tenantName ?? "—"}</div>
          </div>
          <button type="button" className="sidebar-close-btn" onClick={closeSidebar} aria-label="Fermer">
            <X size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeSidebar}
              className={({ isActive }) => "nav-item" + (isActive ? " is-active" : "")}
            >
              {item.icon}
              <span>{t(item.i18nKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item" type="button" onClick={() => logout()}>
            <LogOut size={16} />
            <span>{t("nav_logout")}</span>
          </button>

          <button className="theme-toggle" type="button" onClick={toggleTheme}>
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === "dark" ? t("theme_to_light") : t("theme_to_dark")}</span>
          </button>

          <select
            className="lang-selector"
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
          </select>

          <div className="powered-footer">
            <a href="https://amsolutions.tech" target="_blank" rel="noopener noreferrer">
              {t("powered_by_full")}
            </a>
          </div>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
